
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createProduct, fetchProducts } from '../../redux/actions/productActions';
import { fetchCategories } from '../../redux/actions/categoryActions';
import Loader from '../shared/Loader';
import toast from 'react-hot-toast';

const AddProductModal = ({ isOpen, toggleModal }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    size: [],
    color: [],
    colorName: [],
    stock: '',
    isActive: true,
    category: '',
    discountPrice: '',
    tags: '',
    primaryImage: null,
    secondaryImages: [],
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [imageError, setImageError] = useState('');
  const dispatch = useDispatch();

  const { categories, loading: categoriesLoading, error: categoriesError } = useSelector(state => state.categories);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCategories());
    }
  }, [isOpen, dispatch]);

  const RequiredAsterisk = () => <span className="text-red-500">*</span>;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSizeChange = (e, size) => {
    const newSizes = e.target.checked
      ? [...formData.size, size]
      : formData.size.filter(s => s !== size);
    setFormData({ ...formData, size: newSizes });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const selectedFiles = Array.from(files);
    
    const validFileTypes = ['image/jpeg', 'image/png'];
    const maxSize = 200 * 1024; // 200KB

    let errorMessage = '';
    if (name === 'primaryImage') {
      if (selectedFiles.length > 1) {
        errorMessage = 'You can only upload 1 primary image';
      } else {
        const file = selectedFiles[0];
        if (!validFileTypes.includes(file.type)) {
          errorMessage = 'Only JPG/PNG files allowed';
        } else if (file.size > maxSize) {
          errorMessage = 'File must be less than 200KB';
        } else {
          setFormData({ ...formData, [name]: file });
        }
      }
    } else if (name === 'secondaryImages') {
      if (selectedFiles.length > 5) {
        errorMessage = 'Maximum 5 secondary images allowed';
      } else {
        for (let file of selectedFiles) {
          if (!validFileTypes.includes(file.type)) {
            errorMessage = 'Only JPG/PNG files allowed';
            break;
          } else if (file.size > maxSize) {
            errorMessage = 'Files must be less than 200KB';
            break;
          }
        }
        if (!errorMessage) {
          setFormData({ ...formData, [name]: selectedFiles });
        }
      }
    }

    setImageError(errorMessage);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.price || formData.price <= 0) newErrors.price = 'Valid price is required';
    if (!formData.discountPrice || formData.discountPrice <= 0) newErrors.discountPrice = 'Valid discountPrice is required';
    if (!formData.stock || formData.stock <= 0) newErrors.stock = 'Stock quantity is required';
    if (!formData.primaryImage) newErrors.primaryImage = 'Primary image is required';
    if (!formData.category) newErrors.category = 'Category selection is required';
    if (formData.size.length === 0) newErrors.size = 'At least one size must be selected';
    if (formData.color.length === 0) newErrors.color = 'At least one color must be added';
    
    const colorNameErrors = formData.colorName.filter(name => !name.trim());
    if (colorNameErrors.length > 0) newErrors.colorName = 'Color names are required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const data = new FormData();
    for (const key in formData) {
      if (key === 'secondaryImages') {
        formData.secondaryImages.forEach(file => data.append('secondaryImages', file));
      } else if (key === 'color') {
        formData.color.forEach(color => data.append('color', color));
      } else if (key === 'colorName') {
        formData.colorName.forEach(name => data.append('colorName', name));
      } else {
        data.append(key, formData[key]);
      }
    }

    try {
      await dispatch(createProduct(data));
      toast.success('Product created successfully!');
      dispatch(fetchProducts());
      toggleModal();
    } catch (err) {
      toast.error('Failed to create product');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddColor = () => {
    setFormData({
      ...formData,
      color: [...formData.color, '#000000'],
      colorName: [...formData.colorName, '']
    });
  };

  const handleColorChange = (index, field, value) => {
    const newColors = [...formData.color];
    const newColorNames = [...formData.colorName];
    
    if (field === 'color') {
      newColors[index] = value;
    } else {
      newColorNames[index] = value;
    }

    setFormData({
      ...formData,
      color: newColors,
      colorName: newColorNames
    });
  };

  const handleRemoveColor = (index) => {
    setFormData({
      ...formData,
      color: formData.color.filter((_, i) => i !== index),
      colorName: formData.colorName.filter((_, i) => i !== index)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-xl font-semibold">Add New Product</h3>
          <button
            onClick={toggleModal}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-4">
          {loading || categoriesLoading ? (
            <Loader />
          ) : categoriesError ? (
            <p className="text-red-500">{categoriesError}</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Product Name <RequiredAsterisk />
                </label>
                <input
                  name="name"
                  className={`w-full p-2 border rounded-md ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onChange={handleChange}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  name="description"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  onChange={handleChange}
                  rows="3"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Price <RequiredAsterisk />
                </label>
                <input
                  name="price"
                  type="number"
                  className={`w-full p-2 border rounded-md ${
                    errors.price ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onChange={handleChange}
                />
                {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
              </div>
              {/* Discount Price */}
              <div>
                <label className="block text-sm font-medium mb-1">
                Discount Price <RequiredAsterisk />
                </label>
                <input
                  name="discountPrice"
                  type="number"
                  className={`w-full p-2 border rounded-md ${
                    errors.discountPrice ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onChange={handleChange}
                />
                {errors.discountPrice && <p className="text-red-500 text-sm mt-1">{errors.discountPrice}</p>}
              </div>

              {/* Sizes */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Sizes <RequiredAsterisk />
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {['S', 'M', 'L', 'XL', 'XXL','FREE'].map((size) => (
                    <label key={size} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.size.includes(size)}
                        onChange={(e) => handleSizeChange(e, size)}
                        className="h-4 w-4"
                      />
                      <span>{size}</span>
                    </label>
                  ))}
                </div>
                {errors.size && <p className="text-red-500 text-sm mt-1">{errors.size}</p>}
              </div>

              {/* Colors */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Colors <RequiredAsterisk />
                </label>
                {formData.color.map((color, index) => (
                  <div key={index} className="flex flex-col sm:flex-row gap-2 mb-2">
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => handleColorChange(index, 'color', e.target.value)}
                      className="w-12 h-10"
                    />
                    <input
                      type="text"
                      placeholder="Color name"
                      value={formData.colorName[index]}
                      onChange={(e) => handleColorChange(index, 'colorName', e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveColor(index)}
                      className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddColor}
                  className="mt-2 px-4 py-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200"
                >
                  + Add Color
                </button>
                {errors.color && <p className="text-red-500 text-sm mt-1">{errors.color}</p>}
                {errors.colorName && <p className="text-red-500 text-sm mt-1">{errors.colorName}</p>}
              </div>

              {/* Stock */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Stock <RequiredAsterisk />
                </label>
                <input
                  name="stock"
                  type="number"
                  className={`w-full p-2 border rounded-md ${
                    errors.stock ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onChange={handleChange}
                />
                {errors.stock && <p className="text-red-500 text-sm mt-1">{errors.stock}</p>}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Category <RequiredAsterisk />
                </label>
                <select
                  name="category"
                  className={`w-full p-2 border rounded-md ${
                    errors.category ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onChange={handleChange}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
              </div>

              {/* Images */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Primary Image <RequiredAsterisk />
                  </label>
                  <input
                    type="file"
                    name="primaryImage"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                  <p className="text-sm text-gray-500">1 image (JPG/PNG, max size: 200KB)</p>
                  {errors.primaryImage && <p className="text-red-500 text-sm mt-1">{errors.primaryImage}</p>}
                  {imageError && <p className="text-red-500 text-sm mt-1">{imageError}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Secondary Images</label>
                  <input
                    type="file"
                    name="secondaryImages"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    onChange={handleFileChange}
                    multiple
                    accept="image/*"
                  />
                  <p className="text-sm text-gray-500">Max 5 images (JPG/PNG, max size: 200KB per image)</p>
                  {imageError && <p className="text-red-500 text-sm mt-1">{imageError}</p>}
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={toggleModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Product'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;